import React, { useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RestoreIcon from '@mui/icons-material/Restore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import {
  DEFAULT_SALON_PUBLIC_LAYOUT,
  SALON_SECTION_STYLE_KEYS,
} from '../../../theme/salonPublicLayout';
import SalonTextStyleControls from './SalonTextStyleControls';


const SalonSectionStyleControls = ({
  value = DEFAULT_SALON_PUBLIC_LAYOUT.section_styles,
  errors = {},
  onChange,
  onResetSection,
  disabled = false,
  theme,
  registerFieldRef = () => {},
  expandedKey: controlledExpandedKey,
  onExpandedChange,
}) => {
  const { t } = useTranslation();
  const [internalExpandedKey, setInternalExpandedKey] = useState(null);
  const expandedKey = controlledExpandedKey === undefined
    ? internalExpandedKey
    : controlledExpandedKey;

  const changeExpandedKey = (nextKey) => {
    if (controlledExpandedKey === undefined) setInternalExpandedKey(nextKey);
    onExpandedChange?.(nextKey);
  };

  return (
    <Stack data-testid="salon-section-style-controls" spacing={1}>
      {SALON_SECTION_STYLE_KEYS.map((sectionKey) => {
        const sectionLabel = t(`salon_theme.styles.sections.${sectionKey}`);
        const sectionValue = value?.[sectionKey]
          || DEFAULT_SALON_PUBLIC_LAYOUT.section_styles[sectionKey];
        return (
          <Accordion
            key={sectionKey}
            TransitionProps={{ unmountOnExit: true }}
            expanded={expandedKey === sectionKey}
            onChange={(_event, expanded) => {
              if (!disabled) changeExpandedKey(expanded ? sectionKey : null);
            }}
            disabled={disabled}
            disableGutters
            elevation={0}
            sx={{ border: '1px solid', borderColor: 'divider', '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ fontWeight: 800 }}>{sectionLabel}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={3}>
                <SalonTextStyleControls
                  sectionKey={sectionKey}
                  role="title"
                  value={sectionValue.title}
                  theme={theme}
                  errors={errors?.[sectionKey]?.title}
                  onChange={(field, nextValue) => onChange(
                    sectionKey,
                    'title',
                    field,
                    nextValue,
                  )}
                  disabled={disabled}
                  registerFieldRef={(field, element) => registerFieldRef(
                    `${sectionKey}.title.${field}`,
                    element,
                  )}
                />
                <SalonTextStyleControls
                  sectionKey={sectionKey}
                  role="content"
                  value={sectionValue.content}
                  theme={theme}
                  errors={errors?.[sectionKey]?.content}
                  onChange={(field, nextValue) => onChange(
                    sectionKey,
                    'content',
                    field,
                    nextValue,
                  )}
                  disabled={disabled}
                  registerFieldRef={(field, element) => registerFieldRef(
                    `${sectionKey}.content.${field}`,
                    element,
                  )}
                />
                <Button
                  type="button"
                  variant="text"
                  startIcon={<RestoreIcon />}
                  aria-label={`${t('salon_theme.styles.reset_section')} — ${sectionLabel}`}
                  onClick={() => onResetSection(sectionKey)}
                  disabled={disabled}
                  sx={{ alignSelf: 'flex-start', justifyContent: 'flex-start' }}
                >
                  {t('salon_theme.styles.reset_section')}
                </Button>
              </Stack>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Stack>
  );
};

export default SalonSectionStyleControls;
